import { Receipt, RotateCw, SearchX } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { formatAmount } from '../money';
import { ReportCards, ReportsTable } from './ReportsLists';
import type { ReportSummary } from './types';
import { type NotePdf, useNotePdf } from './use-report-pdf';
import type { ReportsListState } from './use-reports-list';
import { sumAmounts } from './use-reports-view';

type Props = {
  readonly state: ReportsListState;
  readonly shown: readonly ReportSummary[];
  readonly desktop: readonly ReportSummary[];
  readonly phone: readonly ReportSummary[];
  readonly narrowed: boolean;
  readonly showOwner: boolean;
  readonly newReport: ReactNode | undefined;
  readonly onClear: () => void;
  readonly onReload: () => void;
};

/** What sits under the toolbar: the loading, failed, empty, no-result and listed states. */
export function ReportsContent(props: Props) {
  const { t } = useTranslation('expenses');
  const { state, shown } = props;
  // Here, above the early returns: the rows' «···» share one PDF request at a time.
  const pdf = useNotePdf();
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('list.loading')} className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-12 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (state.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={Receipt}
        title={t('reports.loadError')}
        action={
          <Button variant="outline" onClick={props.onReload}>
            {t('list.retry')}
          </Button>
        }
      />
    );
  }
  if (shown.length === 0) {
    return props.narrowed ? (
      <StateMessage
        icon={SearchX}
        title={t('list.noResults.title')}
        text={t('list.noResults.text')}
        action={
          <Button variant="outline" onClick={props.onClear}>
            {t('list.noResults.clear')}
          </Button>
        }
      />
    ) : (
      <StateMessage
        icon={Receipt}
        title={t('reports.empty.title')}
        text={t('reports.empty.text')}
        action={props.newReport}
      />
    );
  }
  return (
    <>
      <ReportsTable items={props.desktop} showOwner={props.showOwner} pdf={pdf} />
      <ReportCards items={props.phone} pdf={pdf} showOwner={props.showOwner} />
      <PdfMessages pdf={pdf} />
    </>
  );
}

/**
 * The list's one count line, beside the pager (#952 M6): which notes of how many, and whose total
 * it is. The server sends no total of every result, so past one page it is this page's.
 */
export function ReportsSummary({
  from,
  shown,
  count,
}: {
  readonly from: number;
  readonly shown: readonly ReportSummary[];
  readonly count: number;
}) {
  const { t } = useTranslation('expenses');
  const total = formatAmount(sumAmounts(shown.map((r) => r.total)));
  const to = from + shown.length - 1;
  return (
    <span data-testid="reports-footer" className="text-muted-foreground">
      {t(count > shown.length ? 'reports.footer.rangePage' : 'reports.footer.range', {
        from,
        to,
        count,
        total,
      })}
    </span>
  );
}

/** The rows' PDF: «Preparo…» at once (U9), then what went wrong with which note, if anything. */
function PdfMessages({ pdf }: { readonly pdf: NotePdf }) {
  const { t } = useTranslation('expenses');
  if (pdf.busy !== null) {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t('reports.pdf.preparing')}
      </p>
    );
  }
  if (pdf.error === null) return null;
  return (
    <p role="alert" className="text-destructive flex flex-wrap items-center gap-2 text-sm">
      {t('reports.pdf.rowError', {
        number: pdf.error.number,
        reason: t(`reports.pdf.errors.${pdf.error.code}`),
      })}
      {pdf.error.code === 'NOTE_PDF_BUSY' ? (
        <Button
          type="button"
          variant="outline"
          className="min-h-11 @min-[620px]/shell:h-8 @min-[620px]/shell:min-h-0"
          onClick={pdf.retry}
        >
          <RotateCw />
          {t('reports.pdf.retry')}
        </Button>
      ) : null}
    </p>
  );
}
